import { Notify } from 'quasar'

export const notifyService = {
  success: (message, actions = []) => {
    Notify.create({
      message,
      color: 'brand',
      icon: 'check_circle',
      position: 'bottom-right',
      timeout: 3000,
      actions: [...actions, { icon: 'close', color: 'white', handler: () => {} }],
      progress: true,
    })
  },
  info: (message) => {
    Notify.create({
      message,
      color: 'info',
      icon: 'info',
      position: 'bottom-right',
      timeout: 3000,
      actions: [{ icon: 'close', color: 'white', handler: () => {} }],
      progress: true,
    })
  },
  error: (message, group) => {
    Notify.create({
      ...(group && { group }),
      message,
      color: 'warning',
      icon: 'error',
      position: 'bottom-right',
      timeout: 3000,
      actions: [{ icon: 'close', color: 'white', handler: () => {} }],
      progress: true,
    })
  },
  actions: (message, actions = [], config = {}) => {
    Notify.create({
      group: config.group || 'actions',
      message,
      color: config.color || 'info',
      icon: config.icon || 'notifications',
      position: 'bottom-right',
      timeout: 5000,
      actions: [
        {
          icon: 'close',
          color: 'white',
          handler: () => {},
          dense: true,
        },
        ...actions,
      ],
      progress: true,
    })
  },

  autoRefresh: ({
    id,
    message,
    onRefresh,
    onCancel,
    dismissible = true,
    actionLabel = 'Refresh now',
    actionIcon = 'refresh',
  }) => {
    const actions = []
    if (onRefresh) {
      actions.push({
        label: actionLabel,
        color: 'white',
        icon: actionIcon,
        dense: true,
        noCaps: true,
        handler: () => {
          onRefresh()
        },
      })
    }
    if (dismissible) {
      actions.push({
        icon: 'close',
        color: 'white',
        class: 'notify-action-dismiss',
        handler: onCancel,
        dense: true,
      })
    }
    Notify.create({
      group: id,
      message,
      multiLine: true,
      color: 'info',
      icon: 'info',
      position: 'bottom-right',
      timeout: 5000,
      actions,
      progress: true,
    })
  },

  navigate: ({ id, message, icon, onNavigate, actionLabel }) => {
    Notify.create({
      group: id,
      message,
      multiLine: true,
      color: 'info',
      icon,
      position: 'bottom-right',
      timeout: 5000,
      actions: [
        {
          label: actionLabel || 'View here',
          color: 'white',
          icon: 'visibility',
          dense: true,
          noCaps: true,
          handler: () => {
            onNavigate()
          },
        },
        {
          icon: 'close',
          color: 'white',
          class: 'notify-action-dismiss',
          handler: () => {},
          dense: true,
        },
      ],
      progress: true,
    })
  },
}

export const notifyMsgs = {
  unauthorized: 'You are not authorized to access this page.',
  sessionExpired: 'Your session has expired. Please log in again.',
  logoutSuccess: 'Logged out successfully.',
  logoutError: 'Could not logout. Please try again.',

  info: {
    fcmReEnableHelp:
      'To re-enable notifications: Click the lock icon in your browser address bar → Change Notifications from "Block" to "Allow" → Refresh the page',
    pocFcmMessageReceived: 'New FCM message received - Check the log below',
    slackTestMessageRateLimit: (seconds) =>
      `Please wait ${seconds} seconds before sending another test message.`,
  },

  /** Push / navigate / auto-refresh notification copy (FCM handlers) */
  fcm: {
    ticketUpdatedAutoRefresh: 'This request has been updated. Auto refresh in 5s',
    ticketUpdatedNavigate: 'There was an update on your request',
    requestDeletedAutoRefresh: 'This request was deleted just now. Closing in 5s',
    requestDeleted: 'A request was deleted just now',
    requestAndApprovalDeletedAutoRefresh:
      'This request and its approval were deleted just now. Returning to requests in 5s',
    requestAndApprovalDeletedClosing:
      'A request and its approval were deleted just now. Closing in 5s',
    requestAndApprovalDeleted: 'A request and its approval were deleted just now',
    requestApprovedAutoRefresh: 'Your request has been approved. Auto refresh in 5s',
    requestApprovedNavigate: 'Your request has been approved',
    approvalDeletedAutoRefresh:
      'This approval was deleted just now. The request is under review again. Closing in 5s',
    approvalDeletedRequestAutoRefresh: 'This approval was deleted just now. Auto refresh in 5s',
    approvalDeleted: 'An approval was deleted just now. The request is under review again.',
    approvalUpdatedAutoRefresh: 'This approval has been updated. Auto refresh in 5s',
    approvalUpdatedNavigate: 'There was an update on your approval',
    newMessageFromAdmin: (sender) => `New message from ${sender || 'Admin'} on your request`,
  },

  error: {
    unknown: 'Something went wrong, please try again.',
    slackConnect: 'Failed to connect to Slack. Please try again.',
    slackConnectNotFound: 'Your email was not found in the Slack workspace.',
    slackDisconnect: 'Failed to disconnect from Slack. Please try again.',
    slackTestMessage: 'Failed to send test message. Please try again.',
    somethingWentWrong: 'Something went wrong. Please try again.',
    loadAccountData: 'Failed to load account data',
    loadApproval: 'Failed to load approval. Please try again.',
    revokeApproval: 'Failed to revoke approval. Please try again.',
    approveConfirmation: 'Failed to confirm approval. Please try again.',
    fetchMessages: 'Failed to fetch messages. Please try again.',
    createMessage: 'Failed to send message. Please try again.',
    searchEntity: 'Failed to search. Please try again.',
    loadTicket: 'Failed to load request. Please try again.',
    createTicket: 'Failed to create request. Please try again.',
    saveDraft: 'Failed to save draft. Please try again.',
    submitTicket: 'Failed to submit request',
    deleteTicket: 'Failed to delete draft. Please try again.',
    uploadFile: 'Failed to upload file',
    deleteFile: 'Failed to delete file. Please try again.',
    getFileLink: 'Failed to get file link. Please try again.',
    downloadFile: 'Failed to download file. Please try again.',
    openFile: 'Unable to open file. Invalid URL format.',
    ticketUnderReview: 'This ticket is already under review and therefore cannot be edited.',
    copyToken: 'Failed to copy token',
    duplicateTicket: 'Failed to create draft. Please try again.',
    submitSupport: 'Failed to submit support request. Please try again.',
    resetConfirmations: 'Failed to reset confirmations',
    updateTheme: 'Failed to save your theme. Please try again.',
    addAdditionalEmail: 'Failed to send the verification code. Please try again.',
    verifyAdditionalEmail: 'Failed to verify the code. Please try again.',
    removeAdditionalEmail: 'Failed to remove the email address. Please try again.',
    notificationPreferencesUpdate: 'Failed to update notification preferences. Please try again.',
  },

  success: {
    general: 'Success',

    // Files
    downloadFile: 'File was downloaded successfully',
    uploadFile: 'File uploaded successfully',
    deleteFile: 'File deleted successfully',

    // Slack
    slackConnect: 'Connected to Slack successfully.',
    slackDisconnect: 'Disconnected from Slack.',
    slackTestMessage: 'Test message sent to Slack.',

    // Notification preferences
    notificationPreferencesUpdate: 'Notification preferences updated.',

    // Additional emails
    additionalEmailCodeSent: 'A verification code was sent to the email address.',
    additionalEmailVerified: 'Additional email address verified.',
    additionalEmailRemoved: 'Additional email address removed.',

    // Support
    submitSupport: 'Your support request has been submitted. We will get back to you soon.',

    // Tickets
    saveDraft: 'Draft saved successfully',
    deleteDraft: 'Draft deleted successfully',
    submitTicket: 'Request submitted successfully',
    createTicket: 'Request was successfully created',
    duplicateDraft: 'Draft created successfully',
    duplicateDraftNoAnswers: 'Draft created successfully. Please save answers manually.',

    // Approvals / confirmations
    revokeApproval: 'Approval revoked successfully',
    approveConfirmation: 'Approval confirmed successfully',
    approveConfirmationBulk: (count) => `${count} approvals confirmed successfully`,

    // FCM
    tokenSent: 'Token sent successfully',
    tokenCopied: 'Token copied to clipboard',

    // Demo
    resetConfirmations: 'All confirmations reset successfully (Frontend Demo)',
  },
}
